import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

const WIDTH = {
  narrow: 'max-w-[720px]',
  default: 'max-w-[1200px]',
  wide: 'max-w-[1360px]',
  full: 'max-w-none',
} as const;

/** The only page width and padding scale in the product (DESIGN.md §5). */
export function PageContainer({
  width = 'default',
  className,
  children,
}: {
  width?: keyof typeof WIDTH;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('mx-auto w-full min-w-0 px-4 py-6 sm:px-6 lg:px-8 lg:py-8', WIDTH[width], className)}>
      {children}
    </div>
  );
}

/** Display title, one-line description, actions at the end, hairline below. */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-6 border-b border-line pb-5', className)}>
      {breadcrumb ? <div className="mb-2 text-[13px] text-ink-2">{breadcrumb}</div> : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-ink md:text-[28px] md:leading-[1.15]">
            {title}
          </h1>
          {description ? <p className="mt-1 max-w-[65ch] text-[13px] text-ink-2 md:text-sm">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

/** One row of controls above a work area; primary action at the end. */
export function Toolbar({
  start,
  end,
  sticky,
  className,
}: {
  start?: ReactNode;
  end?: ReactNode;
  sticky?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2',
        sticky && 'sticky top-0 z-10 -mx-4 bg-canvas/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-canvas/80 sm:-mx-6 sm:px-6',
        className
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">{start}</div>
      {end ? <div className="flex shrink-0 items-center gap-2">{end}</div> : null}
    </div>
  );
}
